import { Dispatch } from "redux/commonTypes";

import authActions from "redux/ducks/auth/authActions";
import contactsActions from "redux/ducks/contacts/contactsActions";
import profileActions from "redux/ducks/profile/profileActions";
import { commonErrorResponses } from "redux/commonTypes";

import messagesApi from "./messagesApi";
import { setDialogMap, setMessagesMap } from "./messagesMaps";
import {
  actionTypes,
  dialogType,
  folderType,
  messageType,
  previewUserInfo,
  writingMessageType,
  foldersIds,
  countersType,
  MSG_PAGINATION_STEP,
  snippetType,
} from "./messagesTypes";


const Actions = {
  setFolders: (folders: Array<folderType>) => ({
    type: actionTypes.SET_FOLDERS,
    payload: folders,
  }),
  setDialogs: (dialogs: Array<dialogType>, folderId: number) => {
    return {
      type: actionTypes.SET_DIALOGS,
      payload: { dialogs, folderId },
    }
  },
  setMessages: (messages: Array<messageType>) => ({
    type: actionTypes.SET_MESSAGES,
    payload: messages,
  }),
  resetMessages: () => ({
    type: actionTypes.RESET_MESSAGES,
    payload: [],
  }),
  setMessagesMore: (messages: Array<messageType>) => ({
    type: actionTypes.SET_MESSAGES_MORE,
    payload: JSON.parse(JSON.stringify(messages)),
  }),
  setLoading: (status: boolean) => ({
    type: actionTypes.SET_MSG_LOADING,
    payload: status,
  }),
  setLoadingMore: (status: boolean) => ({
    type: actionTypes.SET_MSG_LOADING_MORE,
    payload: status,
  }),
  setUserUnarchived: (id: string) => ({
    type: actionTypes.SET_USER_UNARCHIVED,
    payload: id,
  }),
  setUserPreview: (userPreview: previewUserInfo) => ({
    type: actionTypes.SET_USER_PREVIEW,
    payload: userPreview,
  }),
  setEditingMessage: (idMsg: number) => ({
    type: actionTypes.SET_EDITING_MESSAGE,
    payload: idMsg,
  }),
  resetEditMessage: () => ({
    type: actionTypes.RESET_EDIT_MESSAGE,
  }),
  setReplyingMessage: (msg: messageType) => ({
    type: actionTypes.SET_REPLYING_MESSAGE,
    payload: msg,
  }),
  removeMessage: (idMessage: number) => ({
    type: actionTypes.DELETE_MESSAGE,
    payload: idMessage,
  }),
  // Удалить сообщение (как saga deleteMessage в приле): после успеха убираем
  // из ленты; если оно было последним — обновляем сниппет чата в списке.
  deleteMessage: (idUser: string, idMessage: number, deleteForAll: boolean) => (
    dispatch: Dispatch,
    getState?: () => any
  ) => {
    dispatch(authActions.refresh("deleteMessage"));
    return messagesApi
      .deleteMessage(idUser, idMessage, deleteForAll)
      .then(() => {
        const st = getState?.()?.messagesReducer;
        const list = st?.currentMessages || [];
        const wasLast = list.length && Number(list[list.length - 1].id) === Number(idMessage);
        dispatch(Actions.removeMessage(idMessage));
        if (wasLast && st) {
          // @ts-ignore
          dispatch(Actions.getChatsInFolder(st.folderId));
        }
      })
      .catch((err) => {
        if (err.response?.status == 401) {
          dispatch(authActions.setNeedRefresh(true));
        }
      });
  },
  resetReplyingMessage: () => ({
    type: actionTypes.RESET_REPLYING_MESSAGE,
  }),
  replaceEditMessage: (idMsg: number, newText: string) => ({
    type: actionTypes.REPLACE_EDIT_MESSAGE,
    payload: { idMsg, newText },
  }),
  setIdSelectedMsgs: (idMsg: number[]) => ({
    type: actionTypes.SET_ID_SELECTED_MESSAGES,
    payload: idMsg,
  }),
  replaceIdSelectedMessage: (idMsg: number) => ({
    type: actionTypes.REPLACE_ID_SELECTED_MESSAGES,
    payload: { idMsg },
  }),
  resetSelectedMsgs: () => ({
    type: actionTypes.RESET_ID_SELECTED_MESSAGES,
  }),
  resetCurrentUser: () => ({
    type: actionTypes.RESET_CURRENT_USER,
    payload: {},
  }),
  setEndOfMessages: (status: boolean) => ({
    type: actionTypes.SET_END_OF_MESSAGES,
    payload: status,
  }),
  addNewMessage: (message: messageType) => ({
    type: actionTypes.ADD_NEW_MSG,
    payload: message,
  }),
  setNewMessagesWasDelivered: (tempId: number, realId: number) => ({
    type: actionTypes.SET_MSG_DELIVERED,
    payload: { tempId, realId },
  }),
  setNewMessagesWasNotDelivered: (tempId: number) => ({
    type: actionTypes.SET_MSG_NOT_DELIVERED,
    payload: tempId,
  }),
  setMessagesReaded: (ids: Array<number>) => ({
    type: actionTypes.SET_MSGS_READED,
    payload: ids,
  }),
  setBlackList: (dialogs: Array<dialogType>) => ({
    type: actionTypes.SET_BLACK_LIST,
    payload: dialogs,
  }),
  setAllDialogs: (dialogs: Array<dialogType>) => ({
    type: actionTypes.SET_ALL_DIALOGS,
    payload: dialogs,
  }),
  // Откат оптимистично добавленного нового чата (бэк отклонил первое сообщение).
  removeNewDialog: (idUser: string) => ({
    type: actionTypes.REMOVE_NEW_DIALOG,
    payload: idUser,
  }),
  addNewDialog: (dialog: dialogType) => ({
    type: actionTypes.ADD_NEW_DIALOG,
    payload: dialog,
  }),
  updateFolderCountersRealtime: (counters: countersType) => ({
    type: actionTypes.UPDATE_FOLDERS_COUNTERS_REALTIME,
    payload: counters,
  }),
  updateFolderCountersOnOpenConversation: (
    counters: countersType,
    id: string
  ) => ({
    type: actionTypes.UPDATE_FOLDERS_COUNTERS_OPEN_CONVERSATION,
    payload: { counters, id },
  }),
  setIsNewMsgReceived: (status: boolean) => ({
    type: actionTypes.SET_IS_NEW_MSG_RECEIVED,
    payload: status,
  }),
  setIsOnline: (status: boolean) => ({
    type: actionTypes.SET_IS_ONLINE,
    payload: status,
  }),
  setDialogRead: (id: string, readStatus: boolean) => {
    return {
      type: actionTypes.SET_DIALOG_READED,
      payload: {
        idUser: id, readStatus
      }
    }
  },
  setSnippets: (snippets: Array<any>) => {
    return {
      type: actionTypes.SET_MSG_SNIPPETS,
      payload: snippets,
    };
  },
  setRealtimeSnippets: (snippet: snippetType) => {
    return {
      type: actionTypes.SET_MSG_REALTIME_SNIPPETS,
      payload: snippet,
    };
  },
  setSnippetReaded: (id: string) => {
    return {
      type: actionTypes.SET_SNIPPET_READED,
      payload: id,
    };
  },
  setEditedTextSnippetMessage: (idRecipient: string, editedMsgText: string) => {
    return {
      type: actionTypes.EDITED_TEXT_SNIPPET_MESSAGE,
      payload: { idRecipient, editedMsgText },
    };
  },
  setCounter: (counter: number) => ({
    type: actionTypes.SET_MSG_COUNTER,
    payload: counter,
  }),
  getMsgFromOtherDevice: (
    idMsg: number,
    idUser: string,
    message: messageType
  ) => ({
    type: actionTypes.SET_MSG_FROM_OTHER_DEVICE,
    payload: { idMsg, idUser, message },
  }),
  setIsOpenMessages: (status: boolean) => {
    return {
      type: actionTypes.SET_IS_OPEN_MESSAGES,
      payload: status,
    };
  },
  setMsgText: (payload: string) => {
    return {
      type: actionTypes.SET_MY_MSG_TEXT,
      payload,
    };
  },
  setErrorIdMsgs: (id: number) => {
    return {
      type: actionTypes.SET_ERROR_ID_MESSAGES,
      payload: id,
    };
  },
  setSnippetLoading: (status: boolean) => {
    return {
      type: actionTypes.SET_SNIPPET_LOADING,
      payload: status,
    };
  },
  resetMgsCntForCurrentDialog: (urlId: string) => {
    return {
      type: actionTypes.RESET_MSG_CNT_FOR_CURRENT_DIALOG,
      payload: urlId,
    };
  },
  setLoadingDialogs: (status: boolean) => {
    return {
      type: actionTypes.SET_LOADING_DIALOGS,
      payload: status,
    };
  },
  resetMyMsgText: (status: boolean) => {
    return {
      type: actionTypes.SET_RESET_MY_MESSAGE,
      payload: status,
    };
  },
  updateTextLastEditingMessageInDialogsSidebar: (
    idUser: string,
    newText: string
  ) => {
    return {
      type: actionTypes.SET_UPDATE_TEXT_LAST_EDITING_MESSAGE_IN_DIALOGS_SIDEBAR,
      payload: { idUser, newText },
    };
  },
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getFolders: () => (dispatch: Dispatch) => {
    messagesApi.getFolders()
      .then((resp) => {
        dispatch(Actions.setFolders(resp.data?.fullInfoFolders || []));
      })
      .catch((err) => {
        console.warn("getFolders failed:", err?.response?.status, err?.message);
        dispatch(Actions.setFolders([]));
      });
  },
  getChatsInFolder: (id: number) => (dispatch: Dispatch) => {
    dispatch(Actions.setLoadingDialogs(true))
    messagesApi.getChatsInFolder(id)
      .then(resp => {
        const chats = resp.data?.previewChats
        Array.isArray(chats)
          ? dispatch(Actions.setDialogs(setDialogMap(chats), id))
          : dispatch(Actions.setDialogs([], id));
      })
      .catch((err) => {
        console.warn("getChatsInFolder failed:", err?.response?.status, err?.message)
        dispatch(Actions.setDialogs([], id));
      })
      .finally(() => {
        dispatch(Actions.setLoadingDialogs(false));
      });
  },
  getMessagesFirst: (idUser: string) => (dispatch: Dispatch) => {
    dispatch(Actions.setLoading(true));

    messagesApi
      .getMessagesFirst(idUser)
      .then((resp) => {
        if (Array.isArray(resp.data.messages)) {
          dispatch(Actions.setMessages(setMessagesMap(resp.data.messages)));
        } else {
          dispatch(Actions.setMessages([]));
        }
      })
      .finally(() => {
        dispatch(Actions.setLoading(false));
      });
  },
  getMessagesMore: (idUser: string, idMessageCursor: number) => (
    dispatch: Dispatch
  ) => {
    dispatch(Actions.setLoadingMore(true));
    messagesApi
      .getMessagesMore(idUser, idMessageCursor)
      .then((resp) => {
        if (
          resp.data.messages &&
          Array.isArray(resp.data.messages) &&
          resp.data.messages.length
        ) {
          dispatch(Actions.setMessagesMore(setMessagesMap(resp.data.messages)));
        } else if (
          !resp.data.messages ||
          (Array.isArray(resp.data.messages) &&
            resp.data.messages.length < MSG_PAGINATION_STEP)
        ) {
          dispatch(Actions.setEndOfMessages(true));
        }
      })
      .finally(() => {
        dispatch(Actions.setLoadingMore(false));
      });
  },
  archiveUser: (id: string) => (dispatch: Dispatch) => {
    messagesApi
      .archiveUser(id)
      .then((resp) => {})
      .catch((err) => {
        if (err.response?.status == 401) {
          dispatch(authActions.setNeedRefresh(true));
        }
      });
  },
  unArchiveUser: (id: string) => (dispatch: Dispatch) => {
    messagesApi
      .unArchiveUser(id)
      .then((resp) => {
        dispatch(Actions.setUserUnarchived(id));
      })
      .catch((err) => {
        if (err.response?.status == 401) {
          dispatch(authActions.setNeedRefresh(true));
        }
      });
  },
  writeMessage: (message: writingMessageType, photos?: Array<File>) => (
    dispatch: Dispatch,
    getState?: () => any
  ) => {
    const tempId = Math.random();

    // Ответ (reply): как в приле — replyTo = id сообщения, на которое
    // отвечаем; уходит query-параметром sendMessage. Берём из стора, только
    // если это сообщение из этого же чата, и сразу сбрасываем плашку.
    if (message.replyTo === undefined && getState) {
      const replying = getState()?.messagesReducer?.replyingMessage;
      const peer = String(message.idUser);
      if (
        replying?.id && Number.isInteger(replying.id) &&
        (String(replying.idSender) === peer || String(replying.idRecipient) === peer)
      ) {
        message.replyTo = replying.id;
      }
      replying?.id && dispatch(Actions.resetReplyingMessage());
    }
    
    Array.isArray(photos) && photos.length && (message.mediaType = "Photo");

    // Сообщение показываем СРАЗУ (оптимистично, как в приле), а не после ответа
    // сервера: раньше оно появлялось с задержкой, когда прокрутка вниз уже
    // отработала, — сообщение «прыгало» и могло оказаться за краем.
    let photosTemp: Array<string> = [];
    Array.isArray(photos) &&
      photos.length &&
      (photosTemp = photos.map((el) => URL.createObjectURL(el)));
    dispatch(
      Actions.addNewMessage({
        id: tempId,
        idSender: message.idProfile,
        idRecipient: message.idUser,
        time: new Date().toUTCString(),
        text: message.text,
        replyTo: message.replyTo,
        photosLarge: photosTemp,
        photosNormal: photosTemp,
        pending: photosTemp.length > 0,
        milliseconds: Date.now()
      })
    );

    messagesApi
      .writeMessage(message, photos)
      .then((response) => {
        dispatch(
          Actions.setNewMessagesWasDelivered(tempId, response.data.idMessage)
        );
        
        dispatch(contactsActions.setMessageStatus(response.status));
        dispatch(contactsActions.setMessageTrigger(true));

        response.data.contactsCount !== undefined && dispatch(contactsActions.setRestContacts(response.data.contactsCount));
        response.data.contactCount === 0 && dispatch(contactsActions.toggleContactMessage(true));
          
        if (response.data.contactCount >= 0) {
          dispatch(contactsActions.setRestContacts(response.data.contactCount));
        } 
      })
      .catch((error) => {
        dispatch(Actions.setNewMessagesWasNotDelivered(tempId));
        // Если с этим человеком переписки ещё не было (это было первое
        // сообщение — например, 3-й чат при лимите), убираем и сам чат из
        // списка: раньше он висел до обновления страницы.
        const rest = getState?.()?.messagesReducer?.currentMessages || [];
        if (!rest.length && message.idUser) {
          dispatch(Actions.removeNewDialog(String(message.idUser)));
        }

        const errorCode = error.response?.data?.errorCode;
        dispatch(contactsActions.setMessageStatus(errorCode));
        dispatch(contactsActions.setMessageTrigger(true));
        if (errorCode === commonErrorResponses.notVerified) {
          // Анти-скам: не выкидываем на экран верификации, а показываем попап
          // как в приле — «дождитесь проверки», если селфи уже отправлено,
          // иначе «подтвердите профиль».
          dispatch(profileActions.requireVerification() as any);
        }
      })
  },
  getUserPreview: (id: string) => (dispatch: Dispatch) => {
    messagesApi
      .getUserPreview(id)
      .then((resp) => {
        dispatch(
          Actions.setUserPreview({
            id: resp.data.previewChat?.previewUser?.previewUserInfo?.id,
            name: resp.data.previewChat?.previewUser?.previewUserInfo?.name,
            birthday:
              resp.data.previewChat?.previewUser?.previewUserInfo?.birthday,
            isOnline: resp.data.previewChat?.previewUser?.onlineStatus?.online,
            photoSmall:
              resp.data.previewChat?.previewUser?.mainPhoto?.photoCropSmall || resp.data.previewChat?.previewUser?.mainPhoto?.photoSmall,
            city:
              resp.data.previewChat?.previewUser?.previewUserInfo?.city?.name,
            country:
              resp.data.previewChat?.previewUser?.previewUserInfo?.country?.name,
            isArchived: resp.data.previewChat?.idFoldersExistInfo?.includes(
              foldersIds.archive
            ),
            isBlocked: resp.data.previewChat?.idFoldersExistInfo?.includes(
              foldersIds.blackList
            ),
            isFav: resp.data.previewChat?.idFoldersExistInfo?.includes(
              foldersIds.favs
            ),
            isMeBlocked: resp.data.previewChat?.userBanProfile,
            mainPhotoVerifiedStatus:
              resp.data.previewChat?.previewUser?.mainPhotoVerifiedStatus,
            systemBanStatus: resp.data.previewChat.userStatus
              ?.systemBanStatus,
            deletedDate: resp.data.previewChat.userStatus
              ?.deletedDate,
          })
        );
      })
      .catch((err) => {
        console.warn("getUserPreview failed:", err?.response?.status, err?.message);
      })
  },
  readMessages: (ids: Array<number>, idUser: string) => (
    dispatch: Dispatch
  ) => {
    messagesApi
      .readMessages(ids, idUser)
      .then((resp) => {
        dispatch(Actions.setMessagesReaded(ids));
        dispatch(Actions.reduceCounter(ids.length));
        dispatch(Actions.setSnippetReaded(idUser));
      })
      .catch((err) => {
        if (err.response?.status == 401) {
          dispatch(authActions.setNeedRefresh(true));
        }
      });
  },
  editMessage: (idUser: string, idMessage: number, newText: string) => (
    dispatch: Dispatch,
    getState: any
  ) => {
    messagesApi
      .editMessage(idUser, idMessage, newText)
      .then((resp) => {
        const messagesReducer = getState().messagesReducer;
        const tempIndexExpectedLastMessage = messagesReducer.currentMessages.findIndex(
          (message: messageType) => message.id === idMessage
        );
        const isLastMessageInConversation =
          messagesReducer.currentMessages.length -
            1 -
            tempIndexExpectedLastMessage ===
          0;
        if (isLastMessageInConversation) {
          dispatch(
            Actions.updateTextLastEditingMessageInDialogsSidebar(
              idUser,
              newText
            )
          );
        }
      })
  },
  addToFavs: (id: string) => (dispatch: Dispatch) => {
    messagesApi.addToFavs(id).catch((err) => {
      if (err.response?.status == 401) {
        dispatch(authActions.setNeedRefresh(true));
      }
    });
  },
  removeFromFavs: (id: string) => (dispatch: Dispatch) => {
    messagesApi.removeFromFavs(id).catch((err) => {
      if (err.response?.status == 401) {
        dispatch(authActions.setNeedRefresh(true));
      }
    });
  },
  reduceCounter: (length: number) => ({
    type: actionTypes.REDUCE_MSG_COUNTER,
    payload: length,
  }),
  deleteDialog: (userId: string, both: boolean) => (dispatch: Dispatch) => {
    messagesApi
      .deleteDialog(userId, both)
      .then((resp) => dispatch(Actions.deleteChatFromDialogs(userId)))
      .catch((err) => {
        if (err.response?.status == 401) {
          dispatch(authActions.setNeedRefresh(true));
        }
      });
  },
  deleteChatFromDialogs: (userId: string) => ({
    type: actionTypes.DELETE_CHAT_FROM_DIALOGS,
    payload: userId,
  }),
  updateDialog: (dialogIndex: number, text: string, idProfile: string, idRecipient: string, time?: string) => ({
    type: actionTypes.UPDATE_DIALOG,
    payload: { dialogIndex, text, time, idProfile, idRecipient },
  }),
  getLostedMessages: (idUser: string) => (dispatch: Dispatch) => {
    idUser &&
      messagesApi
        .getMessagesFirst(idUser)
        .then((resp) => {
          dispatch(
            Actions.updateLostedMessages(setMessagesMap(resp.data.messages))
          );
          dispatch(Actions.setIsNewMsgReceived(true));
          setTimeout(() => {
            dispatch(Actions.setIsNewMsgReceived(false));
          }, 500);
        })
        .catch((err) => null);
  },
  updateLostedMessages: (msgs: Array<messageType>) => ({
    type: actionTypes.UPDATE_LOSTED_MSGS,
    payload: msgs,
  }),
  setFolderId: (id: number) => {
    return {
      type: actionTypes.SET_FOLDER_ID,
      payload: id,
    };
  },
};

export default Actions;
